import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { useScreenSize } from "shared/lib/useScreenSize"
import { IconSize } from "shared/types/Icon"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Checkbox } from "shared/ui/input/select/checkbox/Checkbox"

import { getProductPositionTableProductPositionColumnSizes } from "./getProductPositionTableColumnSizes"
import "./styles/_product-position-table-header.scss"

interface ProductPositionTableHeaderProps {
  hideQuantity?: boolean
  showClarificationRequestColumn?: boolean
  showSelectionCheckbox?: boolean
  columnPreset?: "default" | "selection"
  isAllSelected?: boolean
  isSomeSelected?: boolean
  onSelectAll?: (checked: boolean) => void
  onCollapseAll?: () => void
  onExpandAll?: () => void
}

export const PRODUCT_POSITION_TABLE_CR_HEADER_ID =
  "product-position-table--clarification-request-header"
export const PRODUCT_POSITION_TABLE_POSITION_HEADER_ID =
  "product-position-table--position-header"
export const PRODUCT_POSITION_TABLE_QUANTITY_HEADER_ID =
  "product-position-table--quantity-header"
export const PRODUCT_POSITION_TABLE_STATUS_HEADER_ID =
  "product-position-table--status-header"
export const PRODUCT_POSITION_TABLE_NAME_HEADER_ID =
  "product-position-table--name-header"
export const PRODUCT_POSITION_TABLE_KEY_HEADER_ID =
  "product-position-table--variant-key-header"
export const PRODUCT_POSITION_TABLE_DIMENSIONING_HEADER_ID =
  "product-position-table--dimensioning-header"
export const PRODUCT_POSITION_TABLE_REMARK_HEADER_ID =
  "product-position-table--remark-header"
export const PRODUCT_POSITION_TABLE_ADDONS_HEADER_ID =
  "product-position-table--addons-header"

export const ProductPositionTableHeader = React.memo(
  (props: ProductPositionTableHeaderProps) => {
    const {
      hideQuantity,
      showClarificationRequestColumn,
      showSelectionCheckbox,
      columnPreset,
      isAllSelected,
      isSomeSelected,
      onSelectAll,
      onCollapseAll,
      onExpandAll
    } = props
    const { t } = useTranslation()
    const { isTablet } = useScreenSize()
    const isSelection = columnPreset === "selection"
    const style = useMemo(
      () =>
        getProductPositionTableProductPositionColumnSizes({
          ...props,
          hideVariantKey: isTablet,
          showSelectionCheckbox,
          columnPreset
        }),
      [props, isTablet, showSelectionCheckbox, columnPreset]
    )

    const headerCells: React.HTMLProps<HTMLDivElement>[] = useMemo(() => {
      const result = []

      result.push({
        id: PRODUCT_POSITION_TABLE_POSITION_HEADER_ID,
        className: "position table-header-cell",
        "data-testid": "position-header",
        children: t("Project.Table.PositionNumberSign")
      })
      if (showClarificationRequestColumn) {
        result.push({
          id: PRODUCT_POSITION_TABLE_CR_HEADER_ID,
          className: "clarification-request table-header-cell",
          children: (
            <>
              <span className="visually-hidden">
                {t("ClarificationRequest.Column.Header")}
              </span>
              <Icon
                name={IconName.CLARIFICATION_REQUEST}
                size={IconSize.SMALL}
              />
            </>
          )
        })
      }
      result.push({
        id: PRODUCT_POSITION_TABLE_NAME_HEADER_ID,
        className: "product table-header-cell",
        children: t("General.Name")
      })
      result.push({
        id: PRODUCT_POSITION_TABLE_KEY_HEADER_ID,
        className: "key table-header-cell",
        children: t("Product.Configuration.Configuration")
      })
      if (!hideQuantity && !isSelection) {
        result.push({
          id: PRODUCT_POSITION_TABLE_QUANTITY_HEADER_ID,
          className: "quantity table-header-cell",
          "data-testid": "quantity-header",
          children: t("General.Quantity")
        })
      }
      if (!isSelection) {
        result.push({
          id: PRODUCT_POSITION_TABLE_DIMENSIONING_HEADER_ID,
          className: "table-header-cell visually-hidden",
          children: t("Product.Configuration.Dimensioning")
        })
        result.push({
          id: PRODUCT_POSITION_TABLE_REMARK_HEADER_ID,
          className: "table-header-cell visually-hidden",
          children: t("General.Remark")
        })
        result.push({
          id: PRODUCT_POSITION_TABLE_ADDONS_HEADER_ID,
          className: "table-header-cell visually-hidden",
          children: t("Product.Configuration.Addon.Addons")
        })
        result.push({
          id: PRODUCT_POSITION_TABLE_STATUS_HEADER_ID,
          className: "table-header-cell info",
          children: t("Product.Configuration.Status")
        })
      }
      return result
    }, [hideQuantity, showClarificationRequestColumn, isSelection, t])

    return (
      <div
        id="product-position-table--header"
        role="row"
        className="project-table-header"
        style={style}
      >
        <div />
        {showSelectionCheckbox ? (
          <div
            role="columnheader"
            className="table-row-cell product-position-table__select-all-cell"
          >
            {onSelectAll ? (
              <Checkbox
                value={isAllSelected}
                onChange={onSelectAll}
                aria-label={t("General.SelectAll")}
                ref={node => {
                  if (node) {
                    // `indeterminate` is only reachable on the DOM node, so the
                    // ref callback has to mutate it directly.
                    // eslint-disable-next-line no-param-reassign
                    node.indeterminate = !!isSomeSelected && !isAllSelected
                  }
                }}
              />
            ) : null}
          </div>
        ) : null}
        <div
          role="cell"
          className="product table-row-cell collapse-expand-all-buttons"
        >
          {onCollapseAll ? (
            <Button
              variant={ButtonVariant.TERTIARY}
              aria-label={t("General.CollapseAll")}
              onClick={onCollapseAll}
              className={classNames(
                "table-header-button",
                "table-header-button--collapse"
              )}
            >
              <Icon name={IconName.MINUS} size={IconSize.XXS} />
            </Button>
          ) : null}
          {onExpandAll ? (
            <Button
              variant={ButtonVariant.TERTIARY}
              aria-label={t("General.ExpandAll")}
              onClick={onExpandAll}
              className={classNames(
                "table-header-button",
                "table-header-button--expand"
              )}
            >
              <Icon name={IconName.PLUS} size={IconSize.XXS} />
            </Button>
          ) : null}
        </div>
        {headerCells.map((cell, index) => (
          <div
            {...cell}
            key={cell.id}
            role="columnheader"
            aria-colindex={index + 1}
          />
        ))}
      </div>
    )
  }
)
